/**
 * Who cannot see a column, and whether AI may.
 *
 * A list of the workspace's members with a switch each, and the whole set is
 * written at once -- which is the endpoint's own shape and the right one:
 * "these people" is a statement about all of them, and adding one at a time
 * would let two tabs remove somebody by not mentioning them.
 *
 * The members come from the workspace, not from a member endpoint. `qk.workspaces()`
 * already holds every membership of every workspace this account is in, and the
 * dedicated listing is admin-only -- so reading it here would reach a smaller
 * audience than the people who can set redaction.
 *
 * `MCP access` is the workspace's own restriction on the column rather than one
 * more member on the list: it hides the values from every AI connection, which
 * is why nothing an agent can call is allowed to set it.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { memberLabel, sortMembers } from '@/features/workspace/members'
import { useWorkspace } from '@/features/workspace/queries'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import { AccessSection, McpAccessSection } from '@/features/application/access/sections'
import { useFieldRedaction, useSetFieldRedaction } from './queries'

export function RedactionDialog({
  tableId,
  field,
  onClose,
}: {
  tableId: number
  field: Field
  onClose: () => void
}) {
  const { t } = useTranslation()
  const workspace = useWorkspace(field.workspace_id)
  const redaction = useFieldRedaction(field.id)
  const save = useSetFieldRedaction(tableId, field.id)

  const [hidden, setHidden] = useState<number[] | null>(null)
  const [mcpAccess, setMcpAccess] = useState(true)
  // Filled once the stored set has arrived, and only then: starting from an
  // empty list and correcting it later would show every member as able to see
  // the column for as long as the request took, which is the opposite of what
  // somebody opening this is checking.
  useEffect(() => {
    if (redaction.data === undefined || hidden !== null) return
    setHidden(redaction.data.user_ids)
    setMcpAccess(redaction.data.mcp_access)
  }, [redaction.data, hidden])

  const members = sortMembers(workspace.data?.users ?? [])
  const chosen = hidden ?? []

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('redaction.title')}</DialogTitle>
          <DialogDescription>
            {t('redaction.description', { field: field.name })}
          </DialogDescription>
        </DialogHeader>

        {hidden === null ? (
          <Skeleton className="h-32" />
        ) : (
          <>
            <AccessSection
              title={t('memberAccess.title')}
              description={t('redaction.memberDescription')}
            >
              <ul
                aria-label={t('redaction.title')}
                className="flex max-h-60 flex-col gap-2 overflow-y-auto"
              >
                {members.map((member) => (
                  <li key={member.id} className="flex items-center gap-2">
                    <Switch
                      id={`redact-${member.user_id}`}
                      size="sm"
                      checked={chosen.includes(member.user_id)}
                      onCheckedChange={(next) => {
                        setHidden(
                          next
                            ? [...chosen, member.user_id]
                            : chosen.filter((id) => id !== member.user_id),
                        )
                      }}
                    />
                    <Label htmlFor={`redact-${member.user_id}`} className="font-normal">
                      {memberLabel(member)}
                    </Label>
                  </li>
                ))}
              </ul>
            </AccessSection>

            <McpAccessSection
              id="redact-mcp"
              description={t('redaction.mcpDescription')}
              checked={mcpAccess}
              onCheckedChange={setMcpAccess}
            />
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            disabled={hidden === null || save.isPending}
            onClick={() => {
              save.mutate(
                { user_ids: chosen, mcp_access: mcpAccess },
                { onSuccess: onClose },
              )
            }}
          >
            {t('field.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
